import React from 'react'
import { Card, CardContent, AsyncContentProgress, FloatingActionButton } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { useCommunityEnabled } from '@/modules/comunidade/hooks/useCommunityEnabled'
import { useChatEnabled } from '../hooks/useChatEnabled'
import { useChatPostEnabled } from '../hooks/useChatPostEnabled'
import { useChatList } from '../hooks/useChatList'
import { createChat } from '../services/chatService'
import { ChatHeader } from '../components/ChatHeader'
import { NewChatModal, type NewChatModalSubmitParams } from '../components/NewChatModal'
import { ChatList } from '../components/ChatList'
import { useLocation } from 'react-router-dom'
import { uploadAndAddChatPostAttachments } from '../services/chatPostAttachmentsUploadService'
import { useChatModeratorEnabled } from '../hooks/useChatModeratorEnabled'
import { useChatHeaderWarningText } from '../hooks/useChatHeaderWarningText'
import { Pencil } from 'lucide-react'
import { useFreemiumPlanStatus } from '@/modules/plans/hooks/useFreemiumPlanStatus'

export default function ChatPage() {
  const enabled = useChatEnabled()
  const communityEnabled = useCommunityEnabled()
  const postEnabled = useChatPostEnabled()
  const chatModeratorEnabled = useChatModeratorEnabled(enabled === true)
  const headerWarningText = useChatHeaderWarningText()
  const { profile, user } = useAuth()
  const { isFreemium } = useFreemiumPlanStatus()
  const { add: toast } = useToast()
  const [createOpen, setCreateOpen] = React.useState(false)
  const location = useLocation()

  const currentUserId = user?.id ? String(user.id) : null
  const isMaster = profile?.role === 'master'
  const canCreatePosts = postEnabled === true
  const canDeleteOthers = isMaster || chatModeratorEnabled === true

  const { items, loading, loadingMore, hasMore, error, reload, loadMore } =
    useChatList(25)

  const focusParams = React.useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || '')
      const focusRaw = params.get('focus')
      const msgRaw = params.get('community_message_id')
      const focusId = focusRaw ? Number(String(focusRaw).trim()) : null
      const messageId = msgRaw ? Number(String(msgRaw).trim()) : null
      return {
        focusChatId:
          Number.isFinite(focusId) && (focusId as number) > 0
            ? Math.floor(focusId as number)
            : null,
        focusMessageId:
          Number.isFinite(messageId) && (messageId as number) > 0
            ? Math.floor(messageId as number)
            : null,
      }
    } catch {
      return { focusChatId: null, focusMessageId: null }
    }
  }, [location.search])

  React.useEffect(() => {
    const focusChatId = focusParams.focusChatId
    const focusMessageId = focusParams.focusMessageId
    if (!focusChatId || !focusMessageId) return

    const exists = items.some((it) => Number(it.id) === focusChatId)
    if (exists) return
    if (!hasMore) return
    if (loading || loadingMore) return

    loadMore()
  }, [
    focusParams.focusChatId,
    focusParams.focusMessageId,
    hasMore,
    items,
    loadMore,
    loading,
    loadingMore,
  ])

  if (enabled === null) {
    return (
      <div className="mx-auto mt-8 w-full max-w-3xl">
        <Card>
          <CardContent className="py-10 text-center text-sm text-foreground/70">
            Carregando acesso ao modulo de Chat...
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!enabled) {
    return (
      <div className="mx-auto mt-8 w-full max-w-3xl">
        <Card>
          <CardContent className="py-10 text-center text-sm text-foreground/70">
            Seu plano ou papel atual nao possui acesso ao modulo de Chat.
          </CardContent>
        </Card>
      </div>
    )
  }

  async function handleCreateChat(params: NewChatModalSubmitParams) {
    try {
      const created = await createChat({
        title: params.title,
        html: params.html,
      })
      if (created) {
        if (params.newAttachments.length > 0) {
          const res = await uploadAndAddChatPostAttachments(
            created.id,
            params.newAttachments,
          )
          if (res.failedCount > 0) {
            toast({
              title: 'Post criado, mas alguns anexos falharam',
              description: `Anexos enviados: ${res.uploadedCount}. Falhas: ${res.failedCount}.`,
              variant: 'warning',
              soft: true,
            })
          }
        }
        toast({
          title: 'Post criado com sucesso',
          description: 'Seu post foi publicado para os membros autorizados.',
          variant: 'success',
          soft: true,
        })
        reload()
      }
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error('Erro ao criar post', e)
      toast({
        title: 'Falha ao criar post',
        description: e?.message || 'Ocorreu um erro ao salvar o post. Tente novamente.',
        variant: 'error',
      })
    }
  }

  return (
    <div className="mx-auto mt-8 w-full max-w-5xl">
      <ChatHeader warningText={headerWarningText} />

      {canCreatePosts && (
        <NewChatModal
          open={createOpen}
          onOpenChange={setCreateOpen}
          onSubmit={handleCreateChat}
        />
      )}

      {error && (
        <Card className="mt-4 border border-red-500/40 bg-red-500/5">
          <CardContent className="text-sm text-red-400">{error}</CardContent>
        </Card>
      )}

      <AsyncContentProgress
        loading={loading}
        label="Carregando posts do chat..."
        className="mt-4"
      >
        <ChatList
          items={items}
          communityEnabled={communityEnabled === true}
          currentUserId={currentUserId}
          canDeleteOthers={canDeleteOthers}
          focusChatId={focusParams.focusChatId}
          focusMessageId={focusParams.focusMessageId}
          showFreemiumCard={isFreemium}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={loadMore}
          onReload={reload}
        />
      </AsyncContentProgress>

      {canCreatePosts && (
        <FloatingActionButton
          label="Adicionar post"
          onClick={() => setCreateOpen(true)}
          className="bg-lime-500 text-black hover:bg-lime-400 focus-visible:ring-lime-500"
        >
          <Pencil className="h-6 w-6" />
        </FloatingActionButton>
      )}
    </div>
  )
}
